import { Box, Button, FormHelperText, Icon, Stack } from "@mui/material";
import { type ChangeEvent, useRef, useState } from "react";

import { CancelButton, RemoveButton, SaveButton } from "~/components/Buttons";
import UserAvatar from "~/components/UserAvatar";
import { type PhotoFileValue } from "~/helpers/photoFile";
import { useSnacks } from "~/providers/SnackProvider";
import readProfileImageFile from "~settings/profile/helpers/profileImageFile";
import useProfileImageMutations from "~settings/profile/hooks/useProfileImageMutations";

interface ProfilePictureSectionProps {
	displayName?: null | string;
	imageUrl?: null | string;
}

const ACCEPTED_MIME_ATTR = "image/jpeg,image/png,image/webp";
const AVATAR_PREVIEW_SIZE = 96;

const buildDataUrl = (photo: PhotoFileValue): string => `data:${photo.mime};base64,${photo.image}`;

const ProfilePictureSection = ({ displayName, imageUrl }: ProfilePictureSectionProps) => {
	const [openSnack] = useSnacks();
	const { clearImage, isMutating, setImage } = useProfileImageMutations();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [preview, setPreview] = useState<null | PhotoFileValue>(null);

	const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;

		const result = await readProfileImageFile(file);
		if (!result.value) {
			openSnack(result.error, "error");
			return;
		}
		setPreview(result.value);
	};

	const handleSave = async () => {
		if (!preview) return;
		const saved = await setImage({ image: preview.image, mime: preview.mime });
		if (saved) setPreview(null);
	};

	const handleRemove = async () => {
		await clearImage();
	};

	const shownImageUrl = preview ? buildDataUrl(preview) : imageUrl;
	const actionsDisabled = isMutating;

	return (
		<Stack spacing={1.5}>
			<Stack alignItems="center" direction="row" spacing={2.5}>
				<UserAvatar imageUrl={shownImageUrl} name={displayName} size={AVATAR_PREVIEW_SIZE} />
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
					{preview ? (
						<>
							<SaveButton disabled={actionsDisabled} onClick={handleSave} />
							<CancelButton disabled={actionsDisabled} onClick={() => setPreview(null)} />
						</>
					) : (
						<>
							<Button
								disabled={actionsDisabled}
								onClick={() => fileInputRef.current?.click()}
								startIcon={<Icon>upload</Icon>}
								variant="outlined"
							>
								{imageUrl ? "Change" : "Upload"}
							</Button>
							{imageUrl && <RemoveButton disabled={actionsDisabled} onClick={handleRemove} />}
						</>
					)}
				</Box>
			</Stack>
			<FormHelperText>Upload a JPEG, PNG, or WEBP image up to 5MB. It is cropped to a square.</FormHelperText>
			<input
				accept={ACCEPTED_MIME_ATTR}
				aria-label="Upload profile picture"
				hidden
				onChange={handleFileSelect}
				ref={fileInputRef}
				type="file"
			/>
		</Stack>
	);
};

export default ProfilePictureSection;
